import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Conversation } from "src/types/conversations";
import {
  generateMetricPercentage,
  generateTimeSpent,
} from "src/services/conversationMetrics.service";

import { Col, Row } from "react-bootstrap";
import InfiniteScroll from "src/components/infiniteScroll";
import Thumbnail from "src/components/thumbnail";
import TimeAgo from "react-timeago";
import { NavLink } from "react-router-dom";
import Truncate from "src/components/truncate";
import TruncateWithTooltip from "src/components/truncateWithTooltip";

import _ from "lodash";
import cx from "classnames";
import styles from "./conversationList.module.scss";
import { useConversationAdoption } from "src/redux/actions/adoption/conversation";
import { TRACK_CONVERSATION_LIST_SCREEN_VIEW } from "src/constants/adoption/eventTypes";

interface IConversationsList {
  loadMore: (pageNo: number) => void;
  sortBy: string;
}

function ConversationsList({ loadMore, sortBy }: IConversationsList) {
  //mixpanel events
  const { trackPageViewed } = useConversationAdoption();
  
  useEffect(() => {
       trackPageViewed(TRACK_CONVERSATION_LIST_SCREEN_VIEW)
  }, [])
  
  //selectors
  const { items, loading, pagination } = useSelector((state: any) =>
    _.get(state.conversations, "conversations"),
  );

  //render
  return (
    <InfiniteScroll
      loading={loading}
      loadMore={() => loadMore(pagination.currentPage + 1)}
      loader={() => {
        if (pagination.currentPage < 0) {
          return _.times(3, (index) => <ConversationPlaceholder key={index} />);
        } else {
          return <ConversationPlaceholder />;
        }
      }}
      hasMore={pagination.hasMoreItems}
      className={styles.list}
    >
      {_.size(items) === 0 &&  !loading && <span className={styles.noRecords}>No records found</span>}
      {items.map((conversation: any) => (
        <ListItem
          key={conversation.id}
          sortBy={sortBy}
          conversation={conversation}
        />
      ))}
    </InfiniteScroll>
  );
}

export default ConversationsList;


//components

interface IListItem {
  conversation: Conversation;
  sortBy: string;
}
const ListItem: React.FC<IListItem> = ({ conversation, sortBy }) => {
  const text = _.get(conversation, "subject", "(No Subject)");
  const renderReceipients = () => {
    let recepient = "";
    if (_.size(conversation.recipients) > 0) {
      if (_.get(conversation, "recipients[0].contact")) {
        recepient = _.get(conversation, "recipients[0].contact.firstName", "");
        if (_.size(conversation.recipients) > 1) {
          const totalRecipients = _.get(conversation, "totalRecipients", 0);
          recepient = `${recepient} + ${totalRecipients - 1}`;
        }
        return recepient;
      } else if (_.get(conversation, "recipients[0].visitor")) {
        recepient = _.get(conversation, "recipients[0].visitor.firstName", "");
        if (_.size(conversation.recipients) > 1) {
          const totalRecipients = _.get(conversation, "totalRecipients", 0);
          recepient = `${recepient} + ${totalRecipients - 1}`;
        }
        return recepient;
      } else {
        return `No recepients`;
      }
    }
  };

  const uniqueMailOpens = useMemo(() => {
    return generateMetricPercentage(
      _.get(conversation, "uniqueEmailOpens", 0) /
        _.get(conversation, "totalRecipients", 0),
    );
  }, [conversation.id]);
  const uniqueLinkClicks = useMemo(() => {
    return generateMetricPercentage(
      _.get(conversation, "uniqueLinkClicks", 0) /
        _.get(conversation, "totalRecipients", 0),
    );
  }, [conversation.id]);

  return (
    <NavLink
      to={`/conversations/${conversation.id}`}
      className={({ isActive }) =>
        cx(styles.container, { [styles.active]: isActive })
      }
    >
      <Row className={styles.item}>
        <Col className={styles.iconContainer} xl={2} xxl={2}>
          <Thumbnail
            className={styles.icon}
            media={conversation.icon}
            defaultImage="/images/assets/default_asset.jpg"
          />
        </Col>
        <Col xl={10} xxl={10} className={styles.detailContainer}>
          <div className={styles.subjectContainer}>
            <TruncateWithTooltip
            text={text}
            placement="top"
            lines={1}
            className={styles.subject}
            />
          <i className="paperflite-right-arrow" />
          </div>

          <p className={styles.shareDetails}>
            {sortBy === "created_date" && (
              <span>
                shared <TimeAgo date={conversation.createdDate} live={false} />
              </span>
            )}
            {sortBy !== "created_date" && (
              <span>
                last active{" "}
                <TimeAgo date={conversation.activityDate} live={false} />
              </span>
            )}
          </p>
          <div className={styles.footer}>
        
            <div className={styles.recepients}>
              <Truncate width={200}>{renderReceipients()}</Truncate>
            </div>
            <div className={styles.analytics}>
              <span
                className={cx(styles.metric, {
                  [styles.disabled]: uniqueMailOpens === "0%",
                })}
              >
                <i className="paperflite-mail-open" />
                {uniqueMailOpens !== "0%" && uniqueMailOpens}
              </span>

              <span
                className={cx(styles.metric, {
                  [styles.disabled]: uniqueLinkClicks === "0%",
                })}
              >
                <i className="paperflite-click-rate" />
                {uniqueLinkClicks !== "0%" && uniqueLinkClicks}
              </span>

              <span
                className={cx(styles.metric, {
                  [styles.disabled]: conversation.totalTimeSpent === 0,
                })}
              >
                <i className="paperflite-clock" />
                {generateTimeSpent(_.get(conversation, "totalTimeSpent", 0))}
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </NavLink>
  );
};

const ConversationPlaceholder = () => {
  return (
    <Row className={cx(styles.item, styles.placeholder)}>
      <Col className={styles.iconContainer} xl={2} xxl={2}>
        <div className={cx(styles.icon, styles.block)} />
      </Col>
      <Col xl={10} xxl={10} className={styles.detailContainer}>
        <p className={cx(styles.subject, styles.block)} />
        <p className={cx(styles.shareDetails, styles.block)} />
        <p className={cx(styles.recepients, styles.block)} />
      </Col>
    </Row>
  );
};
